import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { restoreCompany } from '../../api/settings';
import { getEORIValidation } from '../../api/util';

const RestoreCompanyModal = ({ isOpen, setIsOpen, refreshCompanies, company = {} }) => {
  const [companyDetails, setCompanyDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState({});

  const GENERIC_ERROR = 'An unknown error occurred. Please try again later.';

  useEffect(() => {
    if (company && isOpen) {
      setCompanyDetails({
        id: company?.companyId,
        name: company?.companyName,
        eori: company?.companyEori,
      });
    }
  }, [company, isOpen]);

  useEffect(() => {
    setIsSubmitting(false);
    setIsSuccess(false);
    setError('');
    setValidation({});
  }, [isOpen]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    let validationError = '';

    if (value.trim().length === 0) {
      validationError = 'This field is required';
    }

    setValidation((prev) => ({
      ...prev,
      [name]: validationError,
    }));

    setCompanyDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setIsSubmitting(true);

      const eoriValidation = await getEORIValidation(companyDetails.eori, false);

      const response = await restoreCompany(companyDetails.id, companyDetails.name, companyDetails.eori);

      if (response.status === 1) {
        /** error */
        setIsSubmitting(false);
        setError(response.message || GENERIC_ERROR);
      } else {
        /** success */
        await refreshCompanies();
        setIsSubmitting(false);
        setIsSuccess(true);
      }
    } catch {
      setIsSubmitting(false);
      setError(GENERIC_ERROR);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => !isSubmitting && setIsOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          p: 4,
          borderRadius: 5,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Restore Company
        </Typography>

        {isSuccess ? (
          <>
            <Alert sx={{ marginTop: 2, marginBottom: 4 }}>Company has been restored successfully.</Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span></span>
              <Button variant="contained" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingTop: 2, paddingBottom: 4 }}>
              <Typography variant="p" component="p">
                To restore this company, please confirm that the name and EORI number below is correct.
              </Typography>
            </Box>

            {error && (
              <Box sx={{ paddingBottom: 4 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <Box sx={{ paddingBottom: 4 }}>
              <TextField
                id="outlined-basic"
                label="Company Name"
                variant="outlined"
                name="name"
                value={companyDetails.name}
                onChange={handleOnChange}
                disabled={isSubmitting}
                error={!!validation.name}
                helperText={validation.name}
                fullWidth
              />
            </Box>

            <Box sx={{ paddingBottom: 4 }}>
              <TextField
                id="outlined-basic"
                label="EORI Number"
                variant="outlined"
                name="eori"
                value={companyDetails.eori}
                onChange={handleOnChange}
                disabled={isSubmitting}
                error={!!validation.eori}
                helperText={validation.eori}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>

              <Button
                variant="contained"
                loading={isSubmitting}
                onClick={handleSubmit}
                disabled={!!Object.keys(validation).some((field) => validation[field] !== '')}
              >
                Restore
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RestoreCompanyModal;
